import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { CURRENCIES } from '../../utils/currencies';

interface ChartOfAccount {
  id: number;
  code: string;
  name: string;
}

interface BankAccount {
  id: number;
  name: string;
  bank_name: string | null;
  account_number: string | null;
  chart_of_account_id: number;
  chart_of_account: ChartOfAccount;
  currency_code: string;
  is_active: boolean;
}

interface AccountForm {
  name: string;
  bank_name: string;
  account_number: string;
  chart_of_account_id: string;
  currency_code: string;
  is_active: boolean;
}

const emptyForm: AccountForm = { name: '', bank_name: '', account_number: '', chart_of_account_id: '', currency_code: 'VUV', is_active: true };

export function BankAccounts() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<BankAccount[] | null>(null);
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccount[]>([]);
  const [editing, setEditing] = useState<'new' | BankAccount | null>(null);
  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get<BankAccount[]>('/bank-accounts').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<ChartOfAccount[]>('/chart-of-accounts').then((res) => setChartOfAccounts(res.data)).catch(() => setChartOfAccounts([]));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: BankAccount) => {
    setForm({
      name: item.name,
      bank_name: item.bank_name ?? '',
      account_number: item.account_number ?? '',
      chart_of_account_id: String(item.chart_of_account_id),
      currency_code: item.currency_code,
      is_active: item.is_active,
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof AccountForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/bank-accounts', form);
      else if (editing) await api.put(`/bank-accounts/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: BankAccount) => {
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/bank-accounts/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Bank Accounts</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add bank account
        </button>
      </header>
      <p className="muted">Bank accounts and their linked Chart of Accounts entry, used for statement import and reconciliation.</p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Bank</th>
                <th>Account Number</th>
                <th>Chart of Accounts</th>
                <th>Currency</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link to={`/banking/accounts/${item.id}/statement-lines`}>
                      <strong>{item.name}</strong>
                    </Link>
                  </td>
                  <td>{item.bank_name ?? '—'}</td>
                  <td>{item.account_number ?? '—'}</td>
                  <td>
                    {item.chart_of_account.code} — {item.chart_of_account.name}
                  </td>
                  <td>{item.currency_code}</td>
                  <td>
                    <span className={`badge ${item.is_active ? 'badge-active' : 'badge-neutral'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No bank accounts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add bank account' : 'Edit bank account'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              Bank name
              <input value={form.bank_name} onChange={set('bank_name')} />
            </label>
            <label>
              Account number
              <input value={form.account_number} onChange={set('account_number')} />
            </label>
            <label className="span-2">
              Chart of Accounts entry
              <select value={form.chart_of_account_id} onChange={set('chart_of_account_id')} required>
                <option value="">Select an account…</option>
                {chartOfAccounts.map((coa) => (
                  <option key={coa.id} value={coa.id}>
                    {coa.code} — {coa.name}
                  </option>
                ))}
              </select>
              {errors.chart_of_account_id && <small className="error">{errors.chart_of_account_id[0]}</small>}
            </label>
            <label>
              Currency
              <select value={form.currency_code} onChange={set('currency_code')}>
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.is_active} onChange={set('is_active')} />
              Active
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
